import React, { useEffect, useRef } from 'react';
import EditorParent from '@/base-components/EditorParent';
import { Box, Center, Flex, GridItem, SimpleGrid, Spacer } from '@chakra-ui/react';
import { Context } from '@/App';
import { RoutePath, SPACING } from '@/generic/config';
import { useQuery } from 'react-query';
import { QueryKeys } from '@/api/base/react-query';
import { getOverview } from '@/api/order-controller';
import { customerName } from '@/generic/customerName';
import useToast from '@/base-components/hooks/useToast';
import Dropdown from '@/base-components/Dropdown';
import Resource from '@/model/resource';
import { isEqual } from 'lodash';
import TaskCalendar from './TaskCalendar';
import { isDesktop } from 'react-device-detect';
import Text from '@/base-components/Text';
import { OrderHit } from '@/model/order-hit.schema';
import usePalette from '@/base-components/hooks/usePalette';
import { Link } from 'react-router-dom';
import Icon, { Icons } from '@/base-components/Icons';
import { capitalizeFirstLetter } from '@/generic/string-utils';
import Divider from '@/base-components/Divider';

const TasksPage: React.FC = () => {
    const toast = useToast();
    const { translations, resources } = React.useContext(Context);
    const resourcesDropdownItems = resources?.data?.map(resource => ({ id: resource.id, name: resource.code }));
    const [currentResource, setCurrentResource] = React.useState<Resource | null>(null);
    const [filterStartDate, setFilterStartDate] = React.useState<Date>(new Date());
    const lastFetchedAt = useRef<Date | null>(null);
    const lastFetchedParams = useRef<{ startDate: string, endDate: string, resourceId: number | null }>({ startDate: '', endDate: '', resourceId: null });
    const closeToastRef = React.useRef<(() => void) | null>(null);
    const onLoading = () => closeToastRef.current = toast(translations.overview.loading, "", "loading");
    const onCloseToast = () => setTimeout(() => closeToastRef.current?.(), 1000);

    const overviewQuery = useQuery(QueryKeys.overview, () => {
        onLoading();
        return getOverview(filterStartDate, filterStartDate, currentResource?.id ?? null, true);
    }, {
        onSuccess: () => {
            onCloseToast();
            lastFetchedAt.current = new Date();
        }
    });

    useEffect(() => {
        const newFetchparams = {
            startDate: filterStartDate.toISOString().split("T")[0],
            endDate: filterStartDate.toISOString().split("T")[0],
            resourceId: currentResource?.id ?? null,
        };

        if (isEqual(lastFetchedParams.current, newFetchparams)) {
            return; // No need to refetch if parameters haven't changed
        }

        console.log("Fetching overview with params:", newFetchparams, lastFetchedParams.current);

        lastFetchedParams.current = newFetchparams;
        overviewQuery.refetch();
    }, [currentResource, overviewQuery, filterStartDate]);


    return (
        <EditorParent title={translations.tasks.title} description={translations.tasks.description}>
            <SimpleGrid columns={isDesktop ? 10 : 4} gap={SPACING} mt={SPACING} mb={SPACING}>
                <GridItem colSpan={2}>
                    {resourcesDropdownItems && <Dropdown title={translations.model.order.resource} items={resourcesDropdownItems} value={currentResource ? { id: currentResource.id, name: currentResource.code } : null} noSelectionTitle={translations.model.order.allResources} onChange={(item) => setCurrentResource(item ? resources?.data?.find(resource => resource.id === item.id) || null : null)} />}
                </GridItem>
                <GridItem colSpan={2}>
                    <TaskCalendar
                        label={translations.model.order.filterDate}
                        selectedDate={filterStartDate}
                        onSelectDate={setFilterStartDate}
                        resourceId={currentResource?.id ?? null}
                    />
                </GridItem>
            </SimpleGrid>
            <DayHeader date={filterStartDate} />
            <Flex direction="column" gap={SPACING} mt={SPACING} pt={SPACING}>
                {overviewQuery.data?.map(order => (
                    <TaskItem key={order.id} order={order} />
                ))}
            </Flex>
            {(overviewQuery.data?.length ?? 0) === 0 && <Center><Text mt={SPACING}>{translations.tasks.noTasksFound}</Text></Center>}
        </EditorParent>
    );
};

export default TasksPage;

const DayHeader = ({ date }: { date: Date }) => {
    const { language } = React.useContext(Context);
    const locale = language === 'SE' ? 'sv-SE' : 'en-US';
    // const shortDate = date.toLocaleDateString("sv-SE");

    return (
        <Box textAlign="center">
            {/* <Text variant="normal" fontWeight="medium" whiteSpace="nowrap">
                {shortDate}
            </Text> */}
            <Divider />
            <Text variant="title">
                {capitalizeFirstLetter(date.toLocaleDateString(locale, { weekday: 'long' }))}
            </Text>
            <Divider />
        </Box>
    );
};

const TaskItem = ({ order }: { order: OrderHit }) => {
    const { translations } = React.useContext(Context);
    const { colorScheme } = usePalette();
    const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${order.worksiteHit.address}, ${order.worksiteHit.city}, ${order.worksiteHit.postalCode}`)}`;

    return (
        <Box textAlign="start" shadow="sm" p={SPACING * 2} pt={SPACING}>
            <Flex>
                {order.express && (
                    <Text my={SPACING} flex={1} variant="small" fontWeight="bold" bg={colorScheme.warning} textAlign="center" borderRadius={SPACING}>{translations.model.order.express.title}</Text>
                )}
                {order.blocked && (
                    <Text my={SPACING} flex={1} variant="small" fontWeight="bold" bg={colorScheme.danger} textAlign="center" borderRadius={SPACING}>{translations.model.order.cards.holdInfo.hold}</Text>
                )}
            </Flex>
            <Flex gap={SPACING} alignItems="center">
                <Icon icon={Icons.map} />
                <Link to={mapsLink} target="_blank" rel="noopener noreferrer">
                    <Text variant="subtitle" fontWeight="bold">{`${order.worksiteHit.address}, ${order.worksiteHit.city}, ${order.worksiteHit.postalCode}`}</Text>
                </Link>
                <Text variant="subtitle">{order.worksiteHit.externalId ? `(${order.worksiteHit.externalId})` : ''}</Text>
                <Spacer />
                <Link to={`${RoutePath.Orders}/${order.id}?fromTasks=true`} target='_blank'>
                    <Icon size="sm" icon={Icons.openLink} />
                </Link>
            </Flex>
            {order.worksiteHit.contactPhone && <Flex gap={SPACING} alignItems="center" w="fit-content" cursor="pointer" onClick={() => window.location.href = `tel:${order.worksiteHit.contactPhone}`}><Text>{translations.tasks.worksitePhone}: {order.worksiteHit.contactPhone}</Text><Icon icon={Icons.phone} size='xs' /></Flex>}
            <Flex gap={SPACING} alignItems="center" mt={SPACING}>
                <Icon icon={Icons.Customer} />
                <Text fontWeight="semibold">{customerName(order.worksiteHit.customerHit)}</Text>
                <Text>{order.worksiteHit.customerHit?.externalId ? `(${order.worksiteHit.customerHit.externalId})` : ''}</Text>
                <Spacer />
                {order.resource && order.resource.code && <Text fontWeight="semibold">{`${order.resource.code}`}</Text>}
            </Flex>
            {order.worksiteHit.customerHit?.phone && <Flex gap={SPACING} alignItems="center" w="fit-content" cursor="pointer" onClick={() => window.location.href = `tel:${order.worksiteHit.customerHit?.phone}`}><Text>{translations.tasks.customerPhone}: {order.worksiteHit.customerHit?.phone}</Text><Icon icon={Icons.phone} size='xs' /></Flex>}
        </Box>
    );
};